/**
 * Promotions — the owner's prices, applied by the till (migration 086).
 *
 * Two kinds:
 *   · price  — the listed products sell at `promo_price` each (per item) or
 *              `pct` % off (per promotion) while the promotion runs;
 *   · bundle — the listed products, `quantity` of each, sell together for
 *              `bundle_price`; every complete set in the cart gets it, the
 *              rest of the line stays at list. „2 ცალი ₾5-ად" is a bundle of
 *              one product × 2.
 *
 * The result is a discount ON THE LINE (`promo_discount` + the promotion
 * that caused it): unit_price stays the list price, total_price is what is
 * paid. It is neither the cart discount (the cashier's, capped by 084) nor
 * the bonus (the customer's, 085).
 *
 * THE SERVER DECIDES. create_sale recomputes the floor a promotion allows
 * for the lines that claim it and refuses a sale that paid less — this file
 * exists so the till shows what the server will accept. The two must agree
 * on one rule: a line claims at most ONE promotion; bundles are settled
 * first (the biggest saving first, each unit spent once), the rest of the
 * lines get the best price promotion for their product.
 */

export type PromotionKind = 'price' | 'bundle'

export interface PromotionItem {
  product_id: string
  /** bundle: units of this product per set; price kind: ignored (1). */
  quantity: number
  /** price kind: a fixed promo price per unit; NULL = the promotion's pct. */
  promo_price: number | null
}

export interface Promotion {
  id: string
  name: string
  kind: PromotionKind
  pct: number | null
  bundle_price: number | null
  starts_at: string | null
  ends_at: string | null
  active: boolean
  items: PromotionItem[]
  created_at: string
  /** 180 (1.58): this bundle is a product's box price — edited on the product, never on the promotions screen. */
  pack_product_id?: string | null
}

/** What the till hands in: one line per product. */
export interface PromoLine {
  product_id: string
  quantity: number
  /** the list price the line is sold at (retail; promotions never see wholesale) */
  unit_price: number
}

export interface PromoResult {
  promotion_id: string
  promotion_name: string
  kind: PromotionKind
  /** ₾ taken off the WHOLE line */
  promo_discount: number
}

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * The price a cart line sells at: its wholesale price in the cart's wholesale
 * mode when it has one, else the list price. Only a list-priced line enters
 * the promotions. ONE rule for the cart panel, the phone's mini-cart bar and
 * the till's „რამდენი?" window (1.40, Codex).
 */
export const cartUnitPrice = (i: { unit_price: number; wholesale_price?: number | null }, wholesale: boolean): number =>
  wholesale && i.wholesale_price != null ? i.wholesale_price : i.unit_price

/** Running now: switched on, and inside its dates when it has them. */
export function isLive(p: Pick<Promotion, 'active' | 'starts_at' | 'ends_at'>, now: Date = new Date()): boolean {
  if (!p.active) return false
  if (p.starts_at && new Date(p.starts_at) > now) return false
  if (p.ends_at && new Date(p.ends_at) < now) return false
  return true
}

/**
 * The per-unit price a PRICE promotion gives a product, or null when the
 * product is not in it / it would not lower the price. Used by the grid to
 * show „₾4.00 (₾5.80)" on the card, and by applyPromotions.
 */
export function promoUnitPrice(p: Promotion, product_id: string, list: number): number | null {
  if (p.kind !== 'price') return null
  const it = p.items.find(i => i.product_id === product_id)
  if (!it) return null
  const price = it.promo_price != null ? it.promo_price : p.pct ? round2(list * (1 - p.pct / 100)) : list
  return price < list ? Math.max(0, price) : null
}

/** The best live PRICE promotion for a product at its list price. */
export function bestPromoPrice(promotions: Promotion[], product_id: string, list: number, now: Date = new Date()): { price: number; promotion: Promotion } | null {
  let best: { price: number; promotion: Promotion } | null = null
  for (const p of promotions) {
    if (!isLive(p, now)) continue
    const price = promoUnitPrice(p, product_id, list)
    if (price == null) continue
    if (!best || price < best.price) best = { price, promotion: p }
  }
  return best
}

/** Live bundles that mention the product — the grid's „კომბო" tag. */
export function bundlesFor(promotions: Promotion[], product_id: string, now: Date = new Date()): Promotion[] {
  return promotions.filter(p => p.kind === 'bundle' && isLive(p, now) && p.items.some(i => i.product_id === product_id))
}

/**
 * Apply every live promotion to a cart. Returns the discount per product
 * (line); products with no entry pay list.
 */
export function applyPromotions(lines: PromoLine[], promotions: Promotion[], now: Date = new Date()): Map<string, PromoResult> {
  const out = new Map<string, PromoResult>()
  const byProduct = new Map<string, PromoLine>()
  for (const l of lines) {
    // one line per product is the cart's invariant; merge defensively anyway
    const prev = byProduct.get(l.product_id)
    byProduct.set(l.product_id, prev ? { ...prev, quantity: prev.quantity + l.quantity } : { ...l })
  }
  const live = promotions.filter(p => isLive(p, now))
  const claimed = new Set<string>()

  // ── bundles first: the biggest saving first, each line claimed once ──
  const bundles = live
    .filter(p => p.kind === 'bundle' && p.bundle_price != null && p.items.length > 0)
    .map(p => {
      // k = complete sets present; the set's list value from the cart's prices
      let k = Infinity
      let setList = 0
      for (const it of p.items) {
        const line = byProduct.get(it.product_id)
        if (!line || claimed.has(it.product_id)) { k = 0; break }
        k = Math.min(k, Math.floor(line.quantity / it.quantity + 1e-9))
        setList += it.quantity * line.unit_price
      }
      if (!Number.isFinite(k)) k = 0
      const saving = k > 0 ? round2(k * (setList - (p.bundle_price ?? 0))) : 0
      return { p, k, setList, saving }
    })
    .filter(b => b.k > 0 && b.saving > 0)
    .sort((a, b) => b.saving - a.saving)

  for (const b of bundles) {
    // a later bundle may share a product with an earlier winner — skip it
    if (b.p.items.some(it => claimed.has(it.product_id))) continue
    // spread the saving over the set's lines by their share of the set's
    // list value; the last line absorbs the rounding so the sum is exact
    let given = 0
    b.p.items.forEach((it, idx) => {
      const line = byProduct.get(it.product_id)!
      const isLast = idx === b.p.items.length - 1
      const share = isLast
        ? round2(b.saving - given)
        : round2(b.saving * (it.quantity * line.unit_price) / b.setList)
      given = round2(given + share)
      claimed.add(it.product_id)
      out.set(it.product_id, { promotion_id: b.p.id, promotion_name: b.p.name, kind: 'bundle', promo_discount: share })
    })
  }

  // ── then the best price promotion for every unclaimed line ──
  for (const line of byProduct.values()) {
    if (claimed.has(line.product_id)) continue
    const best = bestPromoPrice(live, line.product_id, line.unit_price, now)
    if (!best) continue
    const discount = round2((line.unit_price - best.price) * line.quantity)
    if (discount <= 0) continue
    out.set(line.product_id, { promotion_id: best.promotion.id, promotion_name: best.promotion.name, kind: 'price', promo_discount: discount })
  }
  return out
}

/** Short label for a promotion in lists: „−20%", „₾4.00", „3 ცალი ₾25". */
export function promoSummary(p: Promotion): string {
  if (p.kind === 'bundle') {
    const units = p.items.reduce((s, i) => s + i.quantity, 0)
    return `${units} ${p.items.length === 1 ? 'ცალი' : 'ნივთი'} · ₾${(p.bundle_price ?? 0).toFixed(2)}`
  }
  if (p.pct) return `−${p.pct}%`
  const fixed = p.items.filter(i => i.promo_price != null)
  if (fixed.length === 1) return `₾${fixed[0].promo_price!.toFixed(2)}`
  return `${fixed.length} ფასი`
}
